import { Caption1, makeStyles, mergeClasses, tokens } from "@fluentui/react-components";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useDataTableStyles } from "@/components/data-table";
import { GhostEmptyState } from "@/components/ghost-empty-state";
import { SegmentedControl } from "@/features/resources/SegmentedControl";
import { formatDate } from "@/lib/date-format";
import { MockCaseMenu } from "./MockCaseMenu";
import { SHARE_LINK_CLOSED, SHARE_VIEW_NOTICE, shareViewModel, useMockPlanAccess } from "./mock-plan-access";

const useStyles = makeStyles({
  page: {
    minHeight: "100vh",
    display: "flex",
    flexDirection: "column",
    backgroundColor: tokens.colorNeutralBackground2,
    color: tokens.colorNeutralForeground1,
    fontWeight: tokens.fontWeightRegular,
  },
  stage: {
    flex: 1,
    minHeight: 0,
    display: "flex",
    flexDirection: "column",
  },
  bar: {
    height: "40px",
    display: "flex",
    alignItems: "center",
    gap: tokens.spacingHorizontalS,
    padding: `0 ${tokens.spacingHorizontalL}`,
    backgroundColor: tokens.colorNeutralBackground1,
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  plan: {
    fontSize: tokens.fontSizeBase400,
    fontWeight: tokens.fontWeightRegular,
    color: tokens.colorNeutralForeground1,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  chip: {
    flexShrink: 0,
    height: "20px",
    display: "inline-flex",
    alignItems: "center",
    padding: `0 ${tokens.spacingHorizontalS}`,
    borderRadius: tokens.borderRadiusMedium,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    color: tokens.colorNeutralForeground2,
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightRegular,
  },
  basis: {
    marginLeft: "auto",
    color: tokens.colorNeutralForeground3,
    whiteSpace: "nowrap",
  },
  main: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalS,
    padding: tokens.spacingVerticalL,
    maxWidth: "880px",
    width: "100%",
    margin: "0 auto",
    boxSizing: "border-box",
  },
  notice: {
    color: tokens.colorNeutralForeground3,
  },
});

/** 공유 링크를 열면 보이는 읽기 전용 목업. 아바타와 편집 조작은 없다. */
export function ShareViewPage() {
  const styles = useStyles();
  const table = useDataTableStyles();
  const { token } = useParams();
  const access = useMockPlanAccess();
  const view = shareViewModel(token ?? "", access.shares);
  const [screen, setScreen] = useState(view?.screens[0] ?? "");
  const [forcedClosed, setForcedClosed] = useState(false);

  useEffect(() => {
    const meta = document.createElement("meta");
    meta.name = "robots";
    meta.content = "noindex, nofollow";
    document.head.appendChild(meta);
    return () => meta.remove();
  }, []);

  const firstScreen = view?.screens[0] ?? "";
  useEffect(() => {
    setScreen(firstScreen);
  }, [view?.id, firstScreen]);

  if (!view || forcedClosed) {
    return (
      <div className={styles.page}>
        <header className={styles.bar}>
          <div className={styles.basis}>
            <MockCaseMenu
              cases={[
                { id: "open", label: "열람" },
                { id: "closed", label: "열 수 없음" },
              ]}
              onPick={(id) => setForcedClosed(id === "closed")}
            />
          </div>
        </header>
        <div className={styles.stage}>
          <GhostEmptyState title={SHARE_LINK_CLOSED} />
        </div>
      </div>
    );
  }

  const shown = view.screens.includes(screen) ? screen : (view.screens[0] ?? "");
  const schedule = shown === "일정(간트)";

  return (
    <div className={styles.page}>
      <header className={styles.bar}>
        <div className={styles.plan} title={view.planName}>{view.planName}</div>
        <span className={styles.chip}>읽기 전용</span>
        <Caption1 className={styles.basis}>{formatDate(view.basis, "dateTime")} 기준</Caption1>
        <MockCaseMenu
          cases={[
            { id: "open", label: "열람" },
            { id: "closed", label: "열 수 없음" },
          ]}
          onPick={(id) => setForcedClosed(id === "closed")}
        />
      </header>
      <main className={styles.main}>
        {view.screens.length > 1 ? (
          <SegmentedControl
            ariaLabel="보여 줄 화면"
            value={shown}
            onChange={setScreen}
            options={view.screens.map((item) => ({ value: item, label: item }))}
          />
        ) : null}
        <Caption1 className={styles.notice}>
          {view.name} · {shown} · {view.mode}
        </Caption1>
        {schedule ? (
          <div className={table.frame}>
            <table className={table.table} aria-label="일정">
              <thead>
                <tr>
                  <th className={table.th}>작업</th>
                  <th className={mergeClasses(table.th, table.num)}>시작</th>
                  <th className={mergeClasses(table.th, table.num)}>종료</th>
                  <th className={mergeClasses(table.th, table.num)}>진척</th>
                </tr>
              </thead>
              <tbody>
                {view.rows.map((row) => (
                  <tr key={row.id}>
                    <td className={table.td}>{row.name}</td>
                    <td className={mergeClasses(table.td, table.num)}>{formatDate(row.start, "numeric")}</td>
                    <td className={mergeClasses(table.td, table.num)}>{formatDate(row.end, "numeric")}</td>
                    <td className={mergeClasses(table.td, table.num)}>{row.progress}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className={table.frame}>
            <table className={table.table} aria-label="현황">
              <thead>
                <tr>
                  <th className={table.th}>항목</th>
                  <th className={mergeClasses(table.th, table.num)}>값</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className={table.td}>진척</td>
                  <td className={mergeClasses(table.td, table.num)}>62%</td>
                </tr>
                <tr>
                  <td className={table.td}>일정</td>
                  <td className={mergeClasses(table.td, table.num)}>기준 안</td>
                </tr>
              </tbody>
            </table>
          </div>
        )}
        <Caption1 className={styles.notice}>{SHARE_VIEW_NOTICE}</Caption1>
      </main>
    </div>
  );
}
